import { useEffect } from "react";
import { LOCALES } from "../i18n";
import { useStore } from "../context/Store";

const BRAND = " | Fleurinelle";
// Meta description from a legal/markdown body: drops the leading "Effective <date>." sentence and markup.
export const docExcerpt = (body) => (body || "")
  .split("\n").filter((l) => l.trim() && !l.trim().startsWith("#"))
  .map((l) => l.trim().replace(/^(?:[-*•]|\d+[.)])\s+/, "")).join(" ")
  .replace(/^[\s*]*(effective|last updated|en vigueur|vigente|in vigore|van kracht|geldig|gültig|ساري)[^.]*\.[\s*]*/i, "")
  .replace(/[#*_>`[\]]+/g, "").replace(/\s+/g, " ").trim();
// Same limits the prerenderer applies, so crawlers see one consistent title/description.
const fitTitle = (s) => {
  let t = (s || "").replace(/\s+/g, " ").trim();
  if (t.length > 60 && t.endsWith(BRAND)) t = t.slice(0, -BRAND.length);
  if (t.length > 65) t = t.slice(0, 62).replace(/\s+\S*$/, "").replace(/[\s,—\-|:]+$/, "") + "…";
  return t;
};
const fitDesc = (s) => {
  const d = (s || "").replace(/[#*_>`[\]]+/g, "").replace(/\s+/g, " ").trim();
  return d.length > 160 ? d.slice(0, 157).replace(/\s+\S*$/, "").replace(/[\s,;:—-]+$/, "") + "…" : d;
};

export const Seo = ({ title: rawTitle, description: rawDescription, path, image, type = "website", noindex = false, alternates = null }) => {
  const title = fitTitle(rawTitle);
  const description = fitDesc(rawDescription);
  const { boot } = useStore();
  const base = (boot?.site_url || process.env.REACT_APP_BACKEND_URL).replace(/\/$/, "");
  useEffect(() => {
    // Static metadata belongs only to the first page; the React page renders its own structured data.
    document.head.querySelectorAll('[data-fl-prerender="1"]').forEach(n => n.remove());
    if (title) document.title = title;
    const set = (name, content, attr = "name") => {
      if (!content) return;
      document.head.querySelectorAll(`meta[${attr}="${name}"]`).forEach((node, i) => { if (i) node.remove(); });
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    // robots directive — set every render so private pages stay noindex and public ones stay indexable
    document.head.querySelectorAll('meta[name="robots"]').forEach((node, i) => { if (i) node.remove(); });
    let robots = document.head.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");
    set("description", description);
    set("og:title", title, "property");
    set("og:description", description, "property");
    set("og:type", type, "property");
    set("og:site_name", "Fleurinelle", "property");
    set("og:image", image, "property");
    set("twitter:card", image ? "summary_large_image" : "summary");
    set("twitter:title", title);
    set("twitter:description", description);
    set("twitter:image", image);
    const cleanPath = (path || window.location.pathname).replace(/\/$/, "") || "/";
    set("og:url", base + cleanPath, "property");

    document.head.querySelectorAll('link[rel="alternate"][hreflang], link[rel="canonical"]').forEach((n) => n.remove());
    if (!noindex) {
      const rest = cleanPath.replace(new RegExp(`^/(${LOCALES.join("|")})(?:/|$)`), "");
      [...LOCALES, "x-default"].forEach((l) => {
        const link = document.createElement("link");
        link.rel = "alternate";
        link.hreflang = l;
        const target = l === "x-default" ? "en" : l;
        const alternate = alternates?.[target] || `/${target}${rest ? `/${rest}` : ""}`;
        link.href = `${base}${alternate.replace(/\/$/, "")}`;
        link.setAttribute("data-fl-hreflang", "1");
        document.head.appendChild(link);
      });
      let canon = document.head.querySelector("link[rel=canonical]");
      if (!canon) {
        canon = document.createElement("link");
        canon.rel = "canonical";
        document.head.appendChild(canon);
      }
      canon.href = base + cleanPath;
    }
  }, [title, description, path, image, type, noindex, alternates, base]);
  return null;
};

export const JsonLd = ({ data }) => {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify(data);
    document.head.appendChild(el);
    return () => el.remove();
  }, [data]);
  return null;
};

export default Seo;
